import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import PrivateLogin from "@/pages/PrivateLogin";
import { usePrivateMode, useStripeEnabled } from "@/lib/privateMode";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Session from "@/pages/Session";
import SessionRTC from "@/pages/SessionRTC";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import ResetPin from "@/pages/ResetPin";
import Verify from "@/pages/Verify";
import SupportJoin from "@/pages/SupportJoin";
import SupportCode from "@/pages/SupportCode";
import LegalPage from "@/pages/legal/LegalPage";
import SeoLanding from "@/pages/seo/SeoLanding";
import { SEO_SLUGS } from "@/pages/seo/seoContent";
import { TimerProvider } from "@/context/TimerContext";
import { GlobalTimerBar } from "@/components/support/TimerBar";
import SupportLayout from "@/pages/support/SupportLayout";
import SupportDashboard from "@/pages/support/SupportDashboard";
import Customers from "@/pages/support/Customers";
import CustomerDetail from "@/pages/support/CustomerDetail";
import Tickets from "@/pages/support/Tickets";
import TicketDetail from "@/pages/support/TicketDetail";
import TimeEntries from "@/pages/support/TimeEntries";
import NeedsReview from "@/pages/support/NeedsReview";
import { CookieConsent } from "@/components/CookieConsent";
import OnboardingModal from "@/components/OnboardingModal";
import { Toaster } from "@/components/ui/sonner";

function AppRouter() {
  const priv = usePrivateMode();
  const stripeOn = useStripeEnabled();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={priv ? <PrivateLogin /> : <Login />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/support" element={<SupportCode />} />
      <Route path="/support/join/:token" element={<SupportJoin />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/billing" element={priv || !stripeOn ? <Navigate to="/dashboard" replace /> : <ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/reset-pin" element={<ProtectedRoute><ResetPin /></ProtectedRoute>} />
      <Route path="/session/:machineId" element={<ProtectedRoute><Session /></ProtectedRoute>} />
      <Route path="/session/:machineId/rtc" element={<ProtectedRoute><SessionRTC /></ProtectedRoute>} />
      <Route path="/helpdesk" element={<ProtectedRoute><SupportLayout /></ProtectedRoute>}>
        <Route index element={<SupportDashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:cid" element={<CustomerDetail />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:tid" element={<TicketDetail />} />
        <Route path="time" element={<TimeEntries />} />
        <Route path="needs-review" element={<NeedsReview />} />
      </Route>
      <Route path="/" element={priv ? <PrivateLogin /> : <Landing />} />
      <Route path="/terms" element={<LegalPage slug="terms" />} />
      <Route path="/privacy" element={<LegalPage slug="privacy" />} />
      <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />
      <Route path="/acceptable-use" element={<LegalPage slug="acceptable-use" />} />
      <Route path="/cookies" element={<LegalPage slug="cookies" />} />
      {SEO_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={priv ? <Navigate to="/" replace /> : <SeoLanding slug={slug} />} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TimerProvider>
          <AppRouter />
          <GlobalTimerBar />
        </TimerProvider>
        <OnboardingModal />
        <CookieConsent />
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
